import { BaseApiService } from '@/shared/api/BaseApiService';

export interface Provider {
  id: string;
  displayName: string;
  email: string;
  role: string;
}

export interface ProviderScheduleEntry {
  id: string;
  providerId: string;
  clinicId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

export interface ProviderScheduleOverride {
  id: string;
  providerId: string;
  clinicId: string;
  overrideDate: string;
  startTime: string | null;
  endTime: string | null;
  isAvailable: boolean;
  reason: string;
}

export interface SetProviderScheduleInput {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

export interface CreateScheduleOverrideInput {
  overrideDate: string;
  startTime: string | null;
  endTime: string | null;
  isAvailable: boolean;
  reason: string;
}

export interface ProviderAvailabilitySummary {
  providerId: string;
  displayName: string;
  totalSlots: number;
  firstAvailableAt: string | null;
  status: 'available' | 'limited' | 'full' | 'off';
}

export class ProvidersService extends BaseApiService {
  protected resourcePath = 'providers';

  async list(): Promise<Provider[]> {
    return this.get();
  }

  async getById(id: string): Promise<Provider> {
    return this.get(`/${id}`);
  }

  async getSchedule(id: string, from?: string, to?: string): Promise<ProviderScheduleEntry[]> {
    return this.get(`/${id}/schedule`, { params: { from, to } });
  }

  async setSchedule(id: string, schedules: SetProviderScheduleInput[]): Promise<void> {
    return this.put(`/${id}/schedule`, schedules);
  }

  async addOverride(id: string, input: CreateScheduleOverrideInput): Promise<void> {
    return this.post(`/${id}/overrides`, input);
  }

  async removeOverride(id: string, overrideId: string): Promise<void> {
    return this.delete(`/${id}/overrides/${overrideId}`);
  }

  async getAvailabilitySummary(date: string): Promise<ProviderAvailabilitySummary[]> {
    return this.get('/availability-summary', { params: { date } });
  }
}

export const providersService = new ProvidersService();
